import React, { useState, useEffect } from 'react';
import { X, PackageMinus, AlertCircle } from 'lucide-react';
import { episService } from '../../../services/epis.service';
import SearchableSelect from '../../../components/SearchableSelect';
import type { EpiEstoqueSaldoContract } from '@sstexames/shared-types';

interface BaixaEstoqueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  empresaId: number;
}

export const BaixaEstoqueModal: React.FC<BaixaEstoqueModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  empresaId,
}) => {
  const [loading, setLoading] = useState(false);
  const [saldos, setSaldos] = useState<EpiEstoqueSaldoContract[]>([]);
  const [selectedEpiId, setSelectedEpiId] = useState<number | ''>('');
  const [quantidade, setQuantidade] = useState<number | ''>(1);
  const [tipo, setTipo] = useState<'baixa_avaria' | 'baixa_descarte' | 'baixa_extravio' | 'ajuste_inventario'>('baixa_avaria');
  const [motivo, setMotivo] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !empresaId) return;
    const fetchSaldos = async () => {
      try {
        const data = await episService.getEstoqueSaldos(empresaId);
        setSaldos(data || []);
      } catch (err) {
        console.error('Erro ao carregar estoque:', err);
      }
    };
    fetchSaldos();
  }, [isOpen, empresaId]);

  const selectedSaldo = saldos.find((s) => s.epi_id === Number(selectedEpiId));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!selectedEpiId) {
      setErrorMessage('Selecione o EPI para baixar.');
      return;
    }
    if (!quantidade || Number(quantidade) <= 0) {
      setErrorMessage('Informe uma quantidade válida para a baixa.');
      return;
    }
    if (selectedSaldo && Number(quantidade) > selectedSaldo.saldo_disponivel) {
      setErrorMessage(`Quantidade máxima disponível para baixa é ${selectedSaldo.saldo_disponivel}.`);
      return;
    }
    if (!motivo.trim()) {
      setErrorMessage('Informe a justificativa / motivo da baixa.');
      return;
    }

    try {
      setLoading(true);
      await episService.registrarBaixaAvaria({
        empresa_id: empresaId,
        epi_id: Number(selectedEpiId),
        quantidade: Number(quantidade),
        tipo,
        motivo,
      });

      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error('Erro ao registrar baixa:', err);
      const errorMsg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao registrar baixa no estoque.';
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center border border-rose-100">
              <PackageMinus className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Baixa por Avaria / Perda / Descarte</h3>
              <p className="text-xs text-gray-500">Ajuste de saldo com registro e motivo no Kardex</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errorMessage && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-2.5 rounded-xl text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <SearchableSelect
              label="Equipamento (EPI)"
              required
              options={saldos.map((s) => ({
                id: s.epi_id,
                label: `${s.epi_descricao} (Disponível: ${s.saldo_disponivel} un.)`,
              }))}
              value={selectedEpiId || null}
              onChange={(val) => setSelectedEpiId(val ? Number(val) : '')}
              placeholder="Selecione um EPI do estoque..."
              searchPlaceholder="Buscar EPI por descrição..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Quantidade a Baixar *
              </label>
              <input
                type="number"
                min="1"
                max={selectedSaldo ? selectedSaldo.saldo_disponivel : undefined}
                required
                value={quantidade}
                onChange={(e) => setQuantidade(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 text-gray-800"
              />
              {selectedSaldo && (
                <span className="text-[11px] text-gray-500 mt-1 block">
                  Máx disponível: {selectedSaldo.saldo_disponivel} un.
                </span>
              )}
            </div>

            <div>
              <SearchableSelect
                label="Tipo da Baixa"
                required
                options={[
                  { id: 'baixa_avaria', label: 'Avaria Técnica' },
                  { id: 'baixa_descarte', label: 'Descarte / Fim de Vida' },
                  { id: 'baixa_extravio', label: 'Extravio / Perda' },
                  { id: 'ajuste_inventario', label: 'Ajuste de Inventário' },
                ]}
                value={tipo}
                onChange={(val) =>
                  val &&
                  setTipo(
                    val as
                      | 'baixa_avaria'
                      | 'baixa_descarte'
                      | 'baixa_extravio'
                      | 'ajuste_inventario',
                  )
                }
                placeholder="Selecione o tipo..."
                searchPlaceholder="Buscar tipo de baixa..."
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              Justificativa / Motivo detalhado *
            </label>
            <textarea
              required
              rows={3}
              placeholder="Ex: Equipamento danificado durante inspeção periódica no almoxarifado."
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-primary-500 text-gray-800"
            />
          </div>

          <div className="border-t border-gray-100 pt-4 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors shadow-2xs"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-xl transition-all shadow-sm disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Confirmar Baixa de Estoque'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
